import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../../contexts/AuthContext';
import ActivityTimelineManagement from '../ActivityTimelineManagement';
import ConfirmationModal from '../common/ConfirmationModal';
import { SkeletonBase, SkeletonText, SkeletonTextShort } from '../ui';

interface Company {
  _id: string;
  name: string;
  industry?: string;
}

interface SalesRep {
  _id: string;
  firstName: string;
  lastName: string;
  email?: string;
}

interface Activity {
  _id: string;
  contact_id: string;
  type: 'primo_contatto' | 'risposta' | 'presentazione_aziendale' | 'brief' | 'presentazione_progetto' | 'esito';
  outcome: 'positivo' | 'negativo' | 'in_corso';
  notes: string;
  created_at: string;
  updated_at: string;
}

interface HistoryEntry {
  updatedBy: string;
  updatedAt: string;
  snapshot: any;
  user?: {
    first_name: string;
    last_name: string;
    email: string;
  };
}

interface Contact {
  _id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  role?: string;
  location?: string;
  company?: Company;
  salesRep?: SalesRep;
  company_id?: string | Company;
  sales_rep_id?: string | SalesRep;
  activity?: Activity[];
  last_activity_at?: string;
  status_outcome?: 'nuovo' | 'positivo' | 'negativo' | 'in_corso';

  notes?: string;
  created_at: string;
  updated_at: string;
}

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 2px solid #e5e7eb;
`;

const BackButton = styled.button`
  background: #f3f4f6;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 8px 16px;
  color: #374151;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;
  
  &:hover {
    background: #e5e7eb;
  }
`;

const Title = styled.h1`
  color: #1f2937;
  font-size: 2rem;
  font-weight: 700;
  margin: 0;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 12px;
`;

const ActionButton = styled.button<{ variant?: 'primary' | 'secondary' }>`
  background: ${props => props.variant === 'primary' ? '#667eea' : '#f3f4f6'};
  color: ${props => props.variant === 'primary' ? 'white' : '#374151'};
  border: 1px solid ${props => props.variant === 'primary' ? '#667eea' : '#d1d5db'};
  border-radius: 8px;
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: ${props => props.variant === 'primary' ? '#5a67d8' : '#e5e7eb'};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 30px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const MainContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 30px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  --skeleton-line-height: 22.4px;
`;

const Sidebar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const InfoCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  --skeleton-line-height: 22.4px;
`;

const InfoCardTitle = styled.h3`
  color: #1f2937;
  font-size: 1.125rem;
  font-weight: 600;
  margin: 0 0 15px 0;
`;

const InfoItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #f3f4f6;
  
  &:last-child {
    border-bottom: none;
  }
`;

const InfoLabel = styled.span`
  color: #6b7280;
  font-size: 14px;
  font-weight: 500;
`;

const InfoValue = styled.span`
  color: #1f2937;
  font-size: 14px;
  font-weight: 500;
`;

const LinkButton = styled.button`
  background: none;
  border: none;
  color: #667eea;
  text-decoration: underline;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  
  &:hover {
    color: #5a67d8;
  }
`;

const StatusBadge = styled.span<{ status: string }>`
  background: ${props => {
    switch (props.status) {
      case 'nuovo': return '#f3f4f6';
      case 'positivo': return '#dcfce7';
      case 'negativo': return '#fee2e2';
      case 'in_corso': return '#fef3c7';
      case 'active': return '#10b981';
      case 'inactive': return '#ef4444';
      case 'prospect': return '#f59e0b';
      default: return '#6b7280';
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'nuovo': return '#374151';
      case 'positivo': return '#166534';
      case 'negativo': return '#dc2626';
      case 'in_corso': return '#92400e';
      default: return 'white';
    }
  }};
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  text-transform: capitalize;
`;

const TabsContainer = styled.div`
  margin-bottom: 30px;
`;

const TabsList = styled.div`
  display: flex;
  border-bottom: 2px solid #e5e7eb;
  margin-bottom: 20px;
`;

const TabButton = styled.button<{ active: boolean }>`
  background: none;
  border: none;
  padding: 12px 24px;
  font-size: 16px;
  font-weight: 500;
  color: ${props => props.active ? '#667eea' : '#6b7280'};
  border-bottom: 2px solid ${props => props.active ? '#667eea' : 'transparent'};
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    color: #667eea;
  }
`;

const TabContent = styled.div`
  display: block;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const FormLabel = styled.label`
  display: block;
  color: #374151;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 6px;
`;

const FormInput = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  transition: border-color 0.2s;
  
  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
  
  &:disabled {
    background: #f9fafb;
    color: #6b7280;
  }
`;

const FormTextarea = styled.textarea`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  min-height: 100px;
  resize: vertical;
  transition: border-color 0.2s;
  
  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
  
  &:disabled {
    background: #f9fafb;
    color: #6b7280;
  }
`;

const SkeletonField = styled(SkeletonBase)`
  width: 100%;
  height: 44.4px;
  border-radius: 8px;
`;

const SkeletonTextarea = styled(SkeletonBase)`
  width: 100%;
  height: 100px;
  border-radius: 8px;
`;

const SkeletonPill = styled(SkeletonBase)`
  width: 90px;
  height: 27.2px;
  border-radius: 20px;
`;

const ErrorMessage = styled.div`
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 20px;
`;

const SuccessMessage = styled.div`
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  color: #16a34a;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 20px;
`;

const getStatusLabel = (status: string | undefined): string => {
  switch (status) {
    case 'nuovo': return 'Nuovo';
    case 'positivo': return 'Positivo';
    case 'negativo': return 'Negativo';
    case 'in_corso': return 'In Corso';
    default: return 'Nuovo';
  }
};

const ContactDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, hasRole } = useAuth();
  const isCreateMode = !id || id === 'new';

  // Helper to get initial state from URL
  const getInitialParam = (key: string, defaultVal: string = '') => {
    return new URLSearchParams(location.search).get(key) || defaultVal;
  };

  const [contact, setContact] = useState<Contact | null>(null);
  const [loading, setLoading] = useState(!isCreateMode);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isEditing, setIsEditing] = useState(isCreateMode);
  const [hasChanges, setHasChanges] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    role: '',
    location: '',
    company_id: '',
    sales_rep_id: '',

    notes: ''
  });
  const [companies, setCompanies] = useState<Company[]>([]);
  const [salesReps, setSalesReps] = useState<SalesRep[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [selectedSalesRep, setSelectedSalesRep] = useState<SalesRep | null>(null);
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [showSalesRepModal, setShowSalesRepModal] = useState(false);
  const [companySearch, setCompanySearch] = useState('');
  const [salesRepSearch, setSalesRepSearch] = useState('');
  // Removed unused companyPage state
  const [activeTab, setActiveTab] = useState<'anagrafica' | 'attivita' | 'history'>(() => {
    const tab = getInitialParam('tab');
    return (tab === 'anagrafica' || tab === 'attivita' || tab === 'history') ? tab : 'anagrafica';
  });

  // Sync tab to URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (activeTab !== 'anagrafica') {
      params.set('tab', activeTab);
    } else {
      params.delete('tab');
    }
    navigate(`?${params.toString()}`, { replace: true });
  }, [activeTab]);
  const [contactHistory, setContactHistory] = useState<HistoryEntry[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [showRecoverModal, setShowRecoverModal] = useState(false);
  const [selectedSnapshot, setSelectedSnapshot] = useState<HistoryEntry | null>(null);
  const isInitialLoading = loading && !contact;

  // Check if user can edit this contact
  const canEditContact = (): boolean => {
    if (isCreateMode) {
      return hasRole(['admin', 'super_user']);
    }
    if (!contact) return false;
    if (hasRole(['admin', 'super_user'])) return true;
    // Regular users can only edit contacts assigned to them as sales rep
    const salesRepId = typeof contact.sales_rep_id === 'object' && contact.sales_rep_id 
      ? contact.sales_rep_id._id 
      : contact.sales_rep_id;
    return salesRepId === user?._id;
  };

  // Check if user can view activities and history tabs
  const canViewActivitiesAndHistory = (): boolean => {
    if (isCreateMode) return false;
    if (!contact) return false;
    if (hasRole(['admin', 'super_user'])) return true;
    // Regular users can only view activities and history for contacts assigned to them as sales rep
    const salesRepId = typeof contact.sales_rep_id === 'object' && contact.sales_rep_id 
      ? contact.sales_rep_id._id 
      : contact.sales_rep_id;
    return salesRepId === user?._id;
  };

  // Scroll to top when entering detail page
  useEffect(() => { window.scrollTo(0, 0); }, []);

  useEffect(() => {
    if (isCreateMode) {
      setLoading(false);
      setIsEditing(true);
      setHasChanges(true);
      return;
    }
    fetchContact();
  }, [id, isCreateMode]);

  // Only fetch companies and sales reps when entering edit mode
  useEffect(() => {
    if (isEditing && companies.length === 0) {
      fetchCompanies();
    }
    if (isEditing && salesReps.length === 0) {
      fetchSalesReps();
    }
  }, [isEditing]);

  const fetchCompanies = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/companies?limit=1000', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const result = await response.json();
        setCompanies(result.data?.companies || []);
      }
    } catch (error) {
      console.error('Failed to fetch companies:', error);
    }
  };

  const fetchSalesReps = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/users?limit=1000', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const result = await response.json();
        const users: SalesRep[] = result.data?.users || [];
        setSalesReps(users);
        if (isCreateMode && user?._id && !selectedSalesRep) {
          const currentUser = users.find((rep) => rep._id === user._id);
          if (currentUser) {
            setSelectedSalesRep(currentUser);
            setFormData(prev => ({ ...prev, sales_rep_id: currentUser._id }));
          }
        }
      }
    } catch (error) {
      console.error('Failed to fetch sales reps:', error);
    }
  };

  const fetchContact = async () => {
    if (isCreateMode || !id) {
      return;
    }
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/contacts/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data?.contact) {
          setContact(result.data.contact);
        } else {
          setError('Invalid response format');
          return;
        }
        const data = result.data.contact;
        setFormData({
          first_name: data.first_name || '',
          last_name: data.last_name || '',
          email: data.email || '',
          phone: data.phone || '',
          role: data.role || '',
          location: data.location || '',
          company_id: getCompanyId(data) || '',
          sales_rep_id: getSalesRepId(data) || '',
          
          notes: data.notes || ''
        });
        
        // Set selected company and sales rep
        if (typeof data.company_id === 'object' && data.company_id) {
          setSelectedCompany(data.company_id);
        }
        if (typeof data.sales_rep_id === 'object' && data.sales_rep_id) {
          setSelectedSalesRep(data.sales_rep_id);
        }
      } else {
        setError('Failed to load contact details');
      }
    } catch (error) {
      setError('An error occurred while loading contact details');
    } finally {
      setLoading(false);
    }
  };

  const fetchContactHistory = async () => {
    try {
      setHistoryLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/contacts/${id}/history`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data?.history) {
          setContactHistory(result.data.history);
        } else {
          setContactHistory([]);
        }
      } else {
        console.error('Failed to load contact history');
        setContactHistory([]);
      }
    } catch (error) {
      console.error('An error occurred while loading contact history:', error);
      setContactHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleRecoverSnapshot = async () => {
    if (!selectedSnapshot) return;
    
    try {
      setError('');
      setSuccess('');
      const token = localStorage.getItem('token');
      
      const response = await fetch(`/api/contacts/${id}/recover-snapshot`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          snapshotData: selectedSnapshot.snapshot,
          snapshotDate: selectedSnapshot.updatedAt
        })
      });

      if (response.ok) {
        setSuccess('Snapshot ripristinato con successo');
        setShowRecoverModal(false);
        setSelectedSnapshot(null);
        fetchContact(); // Refresh contact data
        fetchContactHistory(); // Reload history with new restore entry
      } else {
        const data = await response.json();
        setError(data.message || 'Impossibile ripristinare lo snapshot');
      }
    } catch (error) {
      setError('Si è verificato un errore durante il ripristino dello snapshot');
    }
  };

  const openRecoverModal = (entry: HistoryEntry) => {
    setSelectedSnapshot(entry);
    setShowRecoverModal(true);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    try {
      setError('');
      setSuccess('');
      const token = localStorage.getItem('token');

      if (!formData.first_name.trim() || !formData.last_name.trim()) {
        setError('Nome e cognome sono obbligatori');
        return;
      }
      if (!selectedCompany?._id) {
        setError('Azienda obbligatoria');
        return;
      }
      if (!selectedSalesRep?._id) {
        setError('Commerciale obbligatorio');
        return;
      }
      
      // Prepare data with selected company and sales rep IDs
      const updateData = {
        ...formData,
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        role: formData.role.trim(),
        location: formData.location.trim(),
        notes: formData.notes.trim(),
        company_id: selectedCompany?._id || null,
        sales_rep_id: selectedSalesRep?._id || null
      };

      const endpoint = isCreateMode ? '/api/contacts' : `/api/contacts/${id}`;
      const method = isCreateMode ? 'POST' : 'PUT';

      const response = await fetch(endpoint, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updateData)
      });

      if (response.ok) {
        const result = await response.json();
        const createdId = result?.data?._id || result?.data?.contact?._id;
        setSuccess(isCreateMode ? 'Contatto creato con successo' : 'Contatto aggiornato con successo');
        setHasChanges(false);
        if (isCreateMode && createdId) {
          navigate(`/dashboard/contacts/${createdId}`);
          return;
        }
        setIsEditing(false);
        fetchContact();
      } else {
        const data = await response.json();
        setError(data.message || (isCreateMode ? 'Impossibile creare il contatto' : 'Impossibile aggiornare il contatto'));
      }
    } catch (error) {
      setError(isCreateMode
        ? 'Si è verificato un errore durante la creazione del contatto'
        : 'Si è verificato un errore durante l\'aggiornamento del contatto'
      );
    }
  };

  const handleCancel = () => {
    if (isCreateMode) {
      navigate('/dashboard/contacts');
      return;
    }
    if (contact) {
      setFormData({
        first_name: contact.first_name || '',
        last_name: contact.last_name || '',
        email: contact.email || '',
        phone: contact.phone || '',
        role: contact.role || '',
        location: contact.location || '',
        company_id: getCompanyId(contact) || '',
        sales_rep_id: getSalesRepId(contact) || '',
        
        notes: contact.notes || ''
      });
      
      // Reset selected company and sales rep
      if (typeof contact.company_id === 'object' && contact.company_id) {
        setSelectedCompany(contact.company_id);
      } else {
        setSelectedCompany(null);
      }
      if (typeof contact.sales_rep_id === 'object' && contact.sales_rep_id) {
        setSelectedSalesRep(contact.sales_rep_id);
      } else {
        setSelectedSalesRep(null);
      }
    }
    setIsEditing(false);
    setHasChanges(false);
    setError('');
    setSuccess('');
  };

  const handleDeleteContact = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/contacts/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        setSuccess('Contatto eliminato con successo');
        setTimeout(() => {
          navigate('/dashboard/contacts');
        }, 1500);
      } else {
        const result = await response.json();
        setError(result.message || 'Impossibile eliminare il contatto');
      }
    } catch (error) {
      setError('Si è verificato un errore durante l\'eliminazione del contatto');
    } finally {
      setShowDeleteModal(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getCompanyName = (contact: Contact) => {
    if (typeof contact.company_id === 'object' && contact.company_id) {
      return contact.company_id.name;
    }
    return 'Nessuna Azienda';
  };

  const getSalesRepName = (contact: Contact) => {
    if (typeof contact.sales_rep_id === 'object' && contact.sales_rep_id) {
      return `${contact.sales_rep_id.firstName} ${contact.sales_rep_id.lastName}`;
    }
    return 'Non Assegnato';
  };

  const getCompanyId = (contact: Contact): string | undefined => {
    if (typeof contact.company_id === 'object' && contact.company_id) {
      return contact.company_id._id;
    }
    if (typeof contact.company_id === 'string') {
      return contact.company_id;
    }
    return undefined;
  };

  const getSalesRepId = (contact: Contact): string | undefined => {
     if (typeof contact.sales_rep_id === 'object' && contact.sales_rep_id) {
       return contact.sales_rep_id._id;
     }
     if (typeof contact.sales_rep_id === 'string') {
       return contact.sales_rep_id;
     }
     return undefined;
   };

  // Computed filtered lists for modals (inline JSX below to avoid remount/focus loss)
  const filteredCompanies = companies.filter(company =>
    company.name.toLowerCase().includes(companySearch.toLowerCase())
  );
  const filteredSalesReps = salesReps.filter(rep =>
    `${rep.firstName} ${rep.lastName}`.toLowerCase().includes(salesRepSearch.toLowerCase())
  );

  // Removed unused formatDateDetailed function

  return (
    <Container>
      {!loading && !contact && !isCreateMode && (
        <ErrorMessage>
          Contatto non trovato
        </ErrorMessage>
      )}
      <Header>
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
          <BackButton onClick={() => navigate(-1)}>
            ⬅️ Torna ai Contatti
          </BackButton>
        </div>
        <ActionButtons>
          {isEditing ? (
            <>
              <ActionButton onClick={handleCancel}>
                ❌ Annulla
              </ActionButton>
              <ActionButton 
                variant="primary" 
                onClick={handleSave}
                disabled={!hasChanges}
              >
                {isCreateMode ? '💾 Salva Contatto' : '💾 Salva Modifiche'}
              </ActionButton>
            </>
          ) : (
            canEditContact() && (
              <>
                <ActionButton 
                  variant="primary" 
                  onClick={() => setIsEditing(true)}
                >
                  ✏️ Modifica Contatto
                </ActionButton>
                <ActionButton 
                  onClick={() => setShowDeleteModal(true)}
                  style={{ background: '#dc2626', color: 'white', borderColor: '#dc2626' }}
                >
                  🗑️ Elimina Contatto
                </ActionButton>
              </>
            )
          )}
        </ActionButtons>
      </Header>
      
      <div style={{ borderBottom: '1px solid #e5e7eb', margin: '0 0 20px 0' }}></div>
      
      <Title style={{ marginBottom: '20px' }}>
        {isInitialLoading ? (
          <div style={{ width: '40%', '--skeleton-line-height': '51.2px' } as React.CSSProperties}>
            <SkeletonText />
          </div>
        ) : (
          isCreateMode ? 'Nuovo Contatto' : (contact ? `${contact.first_name} ${contact.last_name}` : 'Caricamento...')
        )}
      </Title>

      {error && <ErrorMessage>{error}</ErrorMessage>}
      {success && <SuccessMessage>{success}</SuccessMessage>}

      {!isCreateMode && (
        <TabsContainer>
        <TabsList>
          <TabButton 
            active={activeTab === 'anagrafica'} 
            onClick={() => setActiveTab('anagrafica')}
          >
            Anagrafica
          </TabButton>
          {canViewActivitiesAndHistory() && (
            <TabButton 
              active={activeTab === 'attivita'} 
              onClick={() => setActiveTab('attivita')}
            >
              Attività
            </TabButton>
          )}
          {canViewActivitiesAndHistory() && (
            <TabButton 
              active={activeTab === 'history'} 
              onClick={() => {
                setActiveTab('history');
                fetchContactHistory();
              }}
            >
              Cronologia
            </TabButton>
          )}
        </TabsList>
        </TabsContainer>
      )}
      <TabContent>
        {(contact || isInitialLoading || isCreateMode) && (
            <>
              {activeTab === 'anagrafica' && (
            <ContentGrid>
              <MainContent>
           <FormGroup>
            <FormLabel>Nome</FormLabel>
            {isInitialLoading ? (
              <SkeletonField />
            ) : (
              <FormInput
                type="text"
                name="first_name"
                value={formData.first_name}
                onChange={handleInputChange}
                disabled={!isEditing}
              />
            )}
          </FormGroup>
          
          <FormGroup>
            <FormLabel>Cognome</FormLabel>
            {isInitialLoading ? (
              <SkeletonField />
            ) : (
              <FormInput
                type="text"
                name="last_name"
                value={formData.last_name}
                onChange={handleInputChange}
                disabled={!isEditing}
              />
            )}
          </FormGroup>
          
          <FormGroup>
            <FormLabel>Email</FormLabel>
            {isInitialLoading ? (
              <SkeletonField />
            ) : (
              <FormInput
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                disabled={!isEditing}
              />
            )}
          </FormGroup>
          
          <FormGroup>
            <FormLabel>Telefono</FormLabel>
            {isInitialLoading ? (
              <SkeletonField />
            ) : (
              <FormInput
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                disabled={!isEditing}
              />
            )}
          </FormGroup>
          
          <FormGroup>
            <FormLabel>Ruolo</FormLabel>
            {isInitialLoading ? (
              <SkeletonField />
            ) : (
              <FormInput
                type="text"
                name="role"
                value={formData.role}
                onChange={handleInputChange}
                disabled={!isEditing}
              />
            )}
          </FormGroup>
          
          <FormGroup>
            <FormLabel>Località</FormLabel>
            {isInitialLoading ? (
              <SkeletonField />
            ) : (
              <FormInput
                type="text"
                name="location"
                value={formData.location}
                onChange={handleInputChange}
                disabled={!isEditing}
                placeholder="es. Milano, MI"
              />
            )}
          </FormGroup>
          
          <FormGroup>
            <FormLabel>Azienda</FormLabel>
            {isInitialLoading ? (
              <SkeletonField />
            ) : isEditing ? (
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  type="button"
                  onClick={() => setShowCompanyModal(true)}
                  style={{
                    flex: 1,
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    background: 'white',
                    textAlign: 'left',
                    cursor: 'pointer',
                    fontSize: '14px',
                    color: selectedCompany ? '#1f2937' : '#6b7280'
                  }}
                >
                  {selectedCompany ? selectedCompany.name : 'Seleziona Azienda'}
                </button>
                {selectedCompany && (
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedCompany(null);
                      setFormData(prev => ({ ...prev, company_id: '' }));
                      setHasChanges(true);
                    }}
                    style={{
                      background: '#ef4444',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '12px',
                      cursor: 'pointer',
                      fontSize: '0.875rem'
                    }}
                  >
                    ×
                  </button>
                )}
              </div>
            ) : (
              <FormInput
                type="text"
                value={getCompanyName(contact!)}
                disabled
              />
            )}
          </FormGroup>
          
          <FormGroup>
            <FormLabel>Commerciale</FormLabel>
            {isInitialLoading ? (
              <SkeletonField />
            ) : isEditing ? (
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  type="button"
                  onClick={() => setShowSalesRepModal(true)}
                  style={{
                    flex: 1,
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    background: 'white',
                    textAlign: 'left',
                    cursor: 'pointer',
                    fontSize: '14px',
                    color: selectedSalesRep ? '#1f2937' : '#6b7280'
                  }}
                >
                  {selectedSalesRep ? `${selectedSalesRep.firstName} ${selectedSalesRep.lastName}` : 'Seleziona Commerciale'}
                </button>
                {selectedSalesRep && (
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedSalesRep(null);
                      setFormData(prev => ({ ...prev, sales_rep_id: '' }));
                      setHasChanges(true);
                    }}
                    style={{
                      background: '#ef4444',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '12px',
                      cursor: 'pointer',
                      fontSize: '0.875rem'
                    }}
                  >
                    ×
                  </button>
                )}
              </div>
            ) : (
              <FormInput
                type="text"
                value={getSalesRepName(contact!)}
                disabled
              />
            )}
          </FormGroup>
          

          
          <FormGroup>
            <FormLabel>Note</FormLabel>
            {isInitialLoading ? (
              <SkeletonTextarea />
            ) : (
              <FormTextarea
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                disabled={!isEditing}
                placeholder="Aggiungi note su questo contatto..."
              />
            )}
          </FormGroup>
                </MainContent>

                {!isCreateMode && (
                <Sidebar>
                  <InfoCard>
                    <InfoCardTitle>Informazioni Contatto</InfoCardTitle>
                    <InfoItem>
                      <InfoLabel>Stato</InfoLabel>
                      {isInitialLoading ? (
                        <SkeletonPill />
                      ) : (
                        <StatusBadge status={contact!.status_outcome || 'nuovo'}>
                          {getStatusLabel(contact!.status_outcome)}
                        </StatusBadge>
                      )}
                    </InfoItem>
                    <InfoItem>
                      <InfoLabel>Email</InfoLabel>
                      {isInitialLoading ? (
                        <SkeletonTextShort />
                      ) : (
                        <InfoValue>
                          {contact!.email ? (
                            <a href={`mailto:${contact!.email}`} style={{ color: '#667eea', textDecoration: 'none' }}>
                              {contact!.email}
                            </a>
                          ) : (
                            'N/A'
                          )}
                        </InfoValue>
                      )}
                    </InfoItem>
                    {!isInitialLoading && contact!.phone && (
                      <InfoItem>
                        <InfoLabel>Telefono</InfoLabel>
                        <InfoValue>
                          <a href={`tel:${contact!.phone}`} style={{ color: '#667eea', textDecoration: 'none' }}>
                            {contact!.phone}
                          </a>
                        </InfoValue>
                      </InfoItem>
                    )}
                  </InfoCard>

                  {(isInitialLoading || contact?.company_id) && (
                    <InfoCard>
                      <InfoCardTitle>Azienda</InfoCardTitle>
                      <InfoItem>
                        <InfoLabel>Azienda</InfoLabel>
                        {isInitialLoading ? (
                          <SkeletonTextShort />
                        ) : (
                          <LinkButton onClick={() => {
                            const companyId = getCompanyId(contact!);
                            if (companyId) navigate(`/dashboard/companies/${companyId}`);
                          }}>
                            {getCompanyName(contact!)}
                          </LinkButton>
                        )}
                      </InfoItem>
                    </InfoCard>
                  )}

                  {(isInitialLoading || contact?.sales_rep_id) && (
                    <InfoCard>
                      <InfoCardTitle>Commerciale</InfoCardTitle>
                      <InfoItem>
                        <InfoLabel>Commerciale</InfoLabel>
                        {isInitialLoading ? (
                          <SkeletonTextShort />
                        ) : (
                          <LinkButton onClick={() => {
                            const salesRepId = getSalesRepId(contact!);
                            if (salesRepId) navigate(`/dashboard/users/${salesRepId}`);
                          }}>
                            {getSalesRepName(contact!)}
                          </LinkButton>
                        )}
                      </InfoItem>
                      {!isInitialLoading && typeof contact!.sales_rep_id === 'object' && contact!.sales_rep_id?.email && (
                        <InfoItem>
                          <InfoLabel>Email</InfoLabel>
                          <InfoValue>
                            <a href={`mailto:${contact!.sales_rep_id.email}`} style={{ color: '#667eea', textDecoration: 'none' }}>
                              {contact!.sales_rep_id.email}
                            </a>
                          </InfoValue>
                        </InfoItem>
                      )}
                    </InfoCard>
                  )}

                  <InfoCard>
                    <InfoCardTitle>Date</InfoCardTitle>
                    <InfoItem>
                      <InfoLabel>Creato il</InfoLabel>
                      {isInitialLoading ? (
                        <SkeletonTextShort />
                      ) : (
                        <InfoValue>{formatDate(contact!.created_at)}</InfoValue>
                      )}
                    </InfoItem>
                    <InfoItem>
                      <InfoLabel>Aggiornato il</InfoLabel>
                      {isInitialLoading ? (
                        <SkeletonTextShort />
                      ) : (
                        <InfoValue>{formatDate(contact!.updated_at)}</InfoValue>
                      )}
                    </InfoItem>
                  </InfoCard>
                </Sidebar>
                )}
              </ContentGrid>
            )}

            {activeTab === 'attivita' && canViewActivitiesAndHistory() && contact && (
              <ActivityTimelineManagement 
                contactId={contact._id} 
                onActivityUpdate={() => {
                  // Refresh contact data when activities are updated
                  fetchContact();
                }}
              />
            )}

            {activeTab === 'history' && canViewActivitiesAndHistory() && (
              <div style={{ padding: '20px' }}>
                <h3 style={{ marginBottom: '20px', color: '#1f2937', fontSize: '18px', fontWeight: '600' }}>
                  Cronologia Modifiche
                </h3>

                {historyLoading ? (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                    Caricamento cronologia...
                  </div>
                ) : contactHistory.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                    Nessuna modifica registrata per questo contatto.
                  </div>
                ) : (
                  <div style={{ position: 'relative' }}>
                    {/* Timeline line */}
                    <div style={{
                      position: 'absolute',
                      left: '20px',
                      top: '0',
                      bottom: '0',
                      width: '2px',
                      background: '#e5e7eb'
                    }} />

                    {contactHistory.map((entry, index) => {
                      const isRestore = entry.snapshot?._restoreAction === 'snapshot_restored';
                      const restoredFromDate = entry.snapshot?._restoredFromDate;

                      // Compute diff: snapshot (before) → next state (after)
                      // entry[0] is newest. The "after" for entry[i] is entry[i-1].snapshot (or current contact for i=0)
                      const trackedFields: { key: string; label: string }[] = [
                        { key: 'first_name', label: 'Nome' },
                        { key: 'last_name', label: 'Cognome' },
                        { key: 'email', label: 'Email' },
                        { key: 'phone', label: 'Telefono' },
                        { key: 'role', label: 'Ruolo' },
                        { key: 'location', label: 'Località' },
                        { key: 'status_outcome', label: 'Stato' },
                        { key: 'notes', label: 'Note' },
                      ];

                      const before = entry.snapshot || {};
                      const after = index === 0
                        ? (contact || {})
                        : (contactHistory[index - 1]?.snapshot || {});

                      const changes = trackedFields
                        .map(({ key, label }) => {
                          const oldVal = (before[key] ?? '') || '';
                          let newVal = (after[key] ?? '') || '';
                          // For status_outcome, display the label
                          if (key === 'status_outcome') {
                            const oldLabel = oldVal ? getStatusLabel(oldVal) : '—';
                            const newLabel = newVal ? getStatusLabel(newVal) : '—';
                            if (oldLabel !== newLabel) return { label, oldVal: oldLabel, newVal: newLabel };
                            return null;
                          }
                          if (String(oldVal) !== String(newVal)) return { label, oldVal: oldVal || '—', newVal: newVal || '—' };
                          return null;
                        })
                        .filter(Boolean) as { label: string; oldVal: string; newVal: string }[];

                      const dotColor = isRestore ? '#f59e0b' : '#667eea';
                      const borderColor = isRestore ? '#fbbf24' : '#e5e7eb';

                      return (
                      <div key={index} style={{
                        position: 'relative',
                        marginBottom: '24px',
                        paddingLeft: '50px'
                      }}>
                        {/* Timeline dot */}
                        <div style={{
                          position: 'absolute',
                          left: '12px',
                          top: '8px',
                          width: '16px',
                          height: '16px',
                          borderRadius: '50%',
                          background: dotColor,
                          border: '3px solid white',
                          boxShadow: `0 0 0 2px ${borderColor}`
                        }} />

                        {/* Entry card */}
                        <div style={{
                          background: isRestore ? '#fffbeb' : 'white',
                          border: `1px solid ${borderColor}`,
                          borderRadius: '8px',
                          padding: '16px',
                          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)'
                        }}>
                          {/* Header */}
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            marginBottom: changes.length > 0 ? '12px' : '0'
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                              {isRestore && (
                                <span style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                  background: '#f59e0b',
                                  color: 'white',
                                  borderRadius: '4px',
                                  padding: '2px 8px',
                                  fontSize: '11px',
                                  fontWeight: '600',
                                  letterSpacing: '0.3px'
                                }}>
                                  ↩ Ripristino
                                </span>
                              )}
                              <div>
                                <div style={{ fontWeight: '600', color: '#1f2937', fontSize: '14px' }}>
                                  {(() => {
                                    if (entry.user && entry.user.first_name && entry.user.last_name) {
                                      return `${entry.user.first_name} ${entry.user.last_name}`;
                                    }
                                    if (!entry.updatedBy || entry.updatedBy === '000000000000000000000000') {
                                      return 'Scrape AI';
                                    }
                                    if (entry.updatedBy && !entry.user) return 'Unknown';
                                    return 'Sistema';
                                  })()}
                                </div>
                                {isRestore && restoredFromDate && (
                                  <div style={{ fontSize: '11px', color: '#92400e', marginTop: '2px' }}>
                                    Ripristinato al punto del {new Date(restoredFromDate).toLocaleString('it-IT', {
                                      day: 'numeric', month: 'short', year: 'numeric',
                                      hour: '2-digit', minute: '2-digit'
                                    })}
                                  </div>
                                )}
                              </div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
                              <div style={{ fontSize: '12px', color: '#6b7280', textAlign: 'right', whiteSpace: 'nowrap' }}>
                                {new Date(entry.updatedAt).toLocaleString('it-IT', {
                                  year: 'numeric', month: 'short', day: 'numeric',
                                  hour: '2-digit', minute: '2-digit'
                                })}
                              </div>
                              {canEditContact() && (
                                <button
                                  onClick={() => openRecoverModal(entry)}
                                  style={{
                                    background: 'none',
                                    color: '#667eea',
                                    border: '1px solid #667eea',
                                    borderRadius: '4px',
                                    padding: '3px 8px',
                                    fontSize: '11px',
                                    cursor: 'pointer',
                                    fontWeight: '500',
                                    whiteSpace: 'nowrap',
                                    transition: 'all 0.2s'
                                  }}
                                  onMouseOver={(e) => { e.currentTarget.style.background = '#667eea'; e.currentTarget.style.color = 'white'; }}
                                  onMouseOut={(e) => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = '#667eea'; }}
                                >
                                  Ripristina
                                </button>
                              )}
                            </div>
                          </div>

                          {/* Diff display */}
                          {changes.length > 0 ? (
                            <div style={{
                              background: isRestore ? '#fef3c7' : '#f9fafb',
                              border: `1px solid ${isRestore ? '#fde68a' : '#e5e7eb'}`,
                              borderRadius: '6px',
                              padding: '10px 12px',
                              fontSize: '13px'
                            }}>
                              {changes.map((change, ci) => (
                                <div key={ci} style={{
                                  display: 'flex',
                                  alignItems: 'baseline',
                                  gap: '8px',
                                  padding: '4px 0',
                                  borderBottom: ci < changes.length - 1 ? '1px solid rgba(0,0,0,0.06)' : 'none'
                                }}>
                                  <span style={{ color: '#6b7280', fontWeight: '500', minWidth: '80px', flexShrink: 0 }}>
                                    {change.label}
                                  </span>
                                  <span style={{ color: '#ef4444', textDecoration: 'line-through', fontSize: '12px', wordBreak: 'break-word' }}>
                                    {change.oldVal}
                                  </span>
                                  <span style={{ color: '#9ca3af', fontSize: '11px', flexShrink: 0 }}>→</span>
                                  <span style={{ color: '#059669', fontWeight: '500', fontSize: '12px', wordBreak: 'break-word' }}>
                                    {change.newVal}
                                  </span>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div style={{
                              background: '#f9fafb',
                              border: '1px solid #e5e7eb',
                              borderRadius: '6px',
                              padding: '8px 12px',
                              fontSize: '12px',
                              color: '#9ca3af',
                              fontStyle: 'italic'
                            }}>
                              Nessuna differenza rilevata nei campi principali
                            </div>
                          )}
                        </div>
                      </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
            </>
          )}
          </TabContent>
      
      {showCompanyModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '24px',
            width: '500px',
            maxHeight: '600px',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, color: '#1f2937', fontSize: '1.25rem', fontWeight: '600' }}>Seleziona Azienda</h3>
              <button
                onClick={() => setShowCompanyModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#6b7280'
                }}
              >
                ×
              </button>
            </div>

            <input
              type="text"
              placeholder="Cerca aziende..."
              value={companySearch}
              onChange={(e) => setCompanySearch(e.target.value)}
              autoFocus
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                marginBottom: '16px',
                fontSize: '14px'
              }}
            />

            <div style={{ flex: 1, overflow: 'auto', maxHeight: '400px' }}>
              {filteredCompanies.map(company => (
                <div
                  key={company._id}
                  onClick={() => {
                    setSelectedCompany(company);
                    setFormData(prev => ({ ...prev, company_id: company._id }));
                    setHasChanges(true);
                    setShowCompanyModal(false);
                    setCompanySearch('');
                  }}
                  style={{
                    padding: '12px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    marginBottom: '8px',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s',
                    backgroundColor: selectedCompany?._id === company._id ? '#f3f4f6' : 'white'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = selectedCompany?._id === company._id ? '#f3f4f6' : 'white'}
                >
                  <div style={{ fontWeight: '500', color: '#1f2937' }}>{company.name}</div>
                  {company.industry && (
                    <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>{company.industry}</div>
                  )}
                </div>
              ))}
              {filteredCompanies.length === 0 && (
                <div style={{ textAlign: 'center', color: '#6b7280', padding: '20px' }}>
                  Nessuna azienda trovata
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {showSalesRepModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '24px',
            width: '500px',
            maxHeight: '600px',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, color: '#1f2937', fontSize: '1.25rem', fontWeight: '600' }}>Seleziona Commerciale</h3>
              <button
                onClick={() => setShowSalesRepModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#6b7280'
                }}
              >
                ×
              </button>
            </div>

            <input
              type="text"
              placeholder="Cerca commerciali..."
              value={salesRepSearch}
              onChange={(e) => setSalesRepSearch(e.target.value)}
              autoFocus
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                marginBottom: '16px',
                fontSize: '14px'
              }}
            />

            <div style={{ flex: 1, overflow: 'auto', maxHeight: '400px' }}>
              {filteredSalesReps.map(rep => (
                <div
                  key={rep._id}
                  onClick={() => {
                    setSelectedSalesRep(rep);
                    setFormData(prev => ({ ...prev, sales_rep_id: rep._id }));
                    setHasChanges(true);
                    setShowSalesRepModal(false);
                    setSalesRepSearch('');
                  }}
                  style={{
                    padding: '12px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    marginBottom: '8px',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s',
                    backgroundColor: selectedSalesRep?._id === rep._id ? '#f3f4f6' : 'white'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = selectedSalesRep?._id === rep._id ? '#f3f4f6' : 'white'}
                >
                  <div style={{ fontWeight: '500', color: '#1f2937' }}>{rep.firstName} {rep.lastName}</div>
                  {rep.email && (
                    <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>{rep.email}</div>
                  )}
                </div>
              ))}
              {filteredSalesReps.length === 0 && (
                <div style={{ textAlign: 'center', color: '#6b7280', padding: '20px' }}>
                  Nessun commerciale trovato
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      
      {contact && (
        <ConfirmationModal
          isOpen={showDeleteModal}
          title="Elimina Contatto"
          message={`Sei sicuro di voler eliminare ${contact.first_name} ${contact.last_name}? Questa azione eliminerà logicamente il contatto e non apparirà più nel sistema. Questa azione può essere annullata da un amministratore.`}
          confirmText="Elimina Contatto"
          cancelText="Annulla"
          onConfirm={handleDeleteContact}
          onCancel={() => setShowDeleteModal(false)}
          variant="danger"
        />
      )}

      <ConfirmationModal
        isOpen={showRecoverModal}
        title="Ripristina Snapshot"
        message={selectedSnapshot ? `Sei sicuro di voler ripristinare il contatto allo stato del ${new Date(selectedSnapshot.updatedAt).toLocaleString('it-IT', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })}? Lo stato attuale verrà salvato come nuovo punto nella cronologia.` : ''}
        confirmText="Ripristina"
        cancelText="Annulla"
        onConfirm={handleRecoverSnapshot}
        onCancel={() => {
          setShowRecoverModal(false);
          setSelectedSnapshot(null);
        }}
        variant="warning"
      />
    </Container>
  );
};

export default ContactDetails;
